import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost, apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import type { OfficePerson } from './arena'

export interface TeamPerson {
  userId: string
  displayName: string
  photoUrl: string | null
  working: boolean
  startedAt: string | null
  /** Named only when the viewer may see the item. */
  card: { id: string; code: string | null; title: string } | null
  /** Working on something the viewer may not see: shown as "a private item", never named. */
  onPrivateItem: boolean
  todayMs: number
  /** Their own list for today, in their words. A linked item the viewer may not see reads as a private item. */
  priorities?: Array<{ id: string; text: string; done: boolean }>
  target: { date: string; text: string | null; minutes: number | null } | null
  /** The team they sit with, if they belong to one. */
  team: string | null
  /** Where they are standing on the office floor right now; null when they are not on that page. */
  pos: OfficeSpot | null
}
export interface OfficeSpot {
  x: number
  y: number
  say: string | null
  sayAt: number
  emote: string | null
  emoteAt: number
  mic: boolean
  cam: boolean
}
export interface TeamDay {
  now: string
  today: string
  me: string
  people: TeamPerson[]
}

export const teamKey = ['worktime', 'team'] as const

/** The browser's offset from UTC, so the server counts "today" in the viewer's own day. */
const tz = () => new Date().getTimezoneOffset()

const fetchTeam = async () => ({ ...(await apiGet<TeamDay>('worktime/team', { tz: tz() })), fetchedAt: Date.now() })

/** `live` is for the office floor, where people moving about make a slow refresh feel dead. */
export function useTeamDay(enabled = true, live: boolean | 'ambient' = false) {
  return useQuery({
    queryKey: teamKey,
    queryFn: fetchTeam,
    enabled,
    refetchInterval: live === true ? 3_500 : live === 'ambient' ? 7_000 : 15_000,
    refetchOnWindowFocus: true,
    staleTime: live === true ? 1_500 : 5_000,
  })
}

/**
 * Who exists and which team they sit with, and nothing that changes as people work or move. The
 * select result is structurally shared, so the same roster comes back (same array) until someone
 * joins or changes team, and the floor is laid out once rather than on every refresh.
 */
export function useOfficeRoster() {
  return useQuery({
    queryKey: teamKey,
    queryFn: fetchTeam,
    select: (d): OfficePerson[] =>
      d.people
        .map((p) => ({ userId: p.userId, displayName: p.displayName, team: p.team }))
        .sort((a, b) => a.userId.localeCompare(b.userId)),
  })
}

export const reportSpot = (body: { x: number; y: number; say?: string; emote?: string; mic?: boolean; cam?: boolean }) =>
  apiPut<{ ok: boolean }>('worktime/office/pos', {
    x: Math.round(body.x),
    y: Math.round(body.y),
    say: body.say ?? null,
    emote: body.emote ?? null,
    mic: body.mic === true,
    cam: body.cam === true,
  })

export interface CallSignal {
  id: string
  from: string
  kind: 'offer' | 'answer' | 'bye'
  gen: string
  data: string
  at: number
}
export const sendSignal = (body: { to: string; kind: CallSignal['kind']; gen: string; data?: string }) =>
  apiPost<{ ok: boolean }>('worktime/office/signal', { ...body, data: body.data ?? '' })
export const pollSignals = async (): Promise<CallSignal[]> => (await apiPost<{ signals: CallSignal[] }>('worktime/office/signals/poll', {})).signals

export function useSetTarget() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { text: string | null; minutes: number | null }) =>
      apiPut<{ target: TeamPerson['target'] }>('worktime/target', { tz: tz(), ...body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamKey }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
